import Dexie, { type Table } from 'dexie'
import type { Mutation, Rejection } from './outbox.js'

/**
 * The local mirror (docs/02 §8).
 *
 * The ENTIRE dataset lives here — roughly 20 MB and under 50k rows over three
 * years, which fits comfortably in IndexedDB. Every screen reads from this
 * store and never from the network; the network is an update channel, not a
 * read path. That is what makes the tablet work with the internet down.
 */
export interface SyncMeta {
  key: string
  value: unknown
}

export class OfficeDb extends Dexie {
  // mirrored server tables
  people!: Table<Record<string, unknown>, string>
  members!: Table<Record<string, unknown>, string>
  guests!: Table<Record<string, unknown>, string>
  presence_events!: Table<Record<string, unknown>, string>
  visits!: Table<Record<string, unknown>, string>
  chore_definitions!: Table<Record<string, unknown>, string>
  chore_instances!: Table<Record<string, unknown>, string>
  chore_swaps!: Table<Record<string, unknown>, string>
  proposal_types!: Table<Record<string, unknown>, string>
  proposals!: Table<Record<string, unknown>, string>
  proposal_votes!: Table<Record<string, unknown>, string>
  resources!: Table<Record<string, unknown>, string>
  bookings!: Table<Record<string, unknown>, string>
  occupancy!: Table<Record<string, unknown>, string>
  queue_entries!: Table<Record<string, unknown>, string>
  matches!: Table<Record<string, unknown>, string>
  ratings!: Table<Record<string, unknown>, string>
  bills!: Table<Record<string, unknown>, string>
  bill_instances!: Table<Record<string, unknown>, string>
  bill_payments!: Table<Record<string, unknown>, string>
  expenses!: Table<Record<string, unknown>, string>
  settlements!: Table<Record<string, unknown>, string>
  food_orders!: Table<Record<string, unknown>, string>
  supply_items!: Table<Record<string, unknown>, string>
  supply_events!: Table<Record<string, unknown>, string>
  notes!: Table<Record<string, unknown>, string>
  tasks!: Table<Record<string, unknown>, string>
  wiki_pages!: Table<Record<string, unknown>, string>
  checklists!: Table<Record<string, unknown>, string>
  checklist_runs!: Table<Record<string, unknown>, string>
  space_mode!: Table<Record<string, unknown>, string>
  ramadan_periods!: Table<Record<string, unknown>, string>
  settings!: Table<Record<string, unknown>, string>
  floor_plans!: Table<Record<string, unknown>, string>
  room_labels!: Table<Record<string, unknown>, string>
  face_templates!: Table<Record<string, unknown>, string>

  // client-only
  outbox!: Table<Mutation, string>
  rejections!: Table<Rejection & { id: string }, string>
  meta!: Table<SyncMeta, string>

  constructor(name = 'office-os') {
    super(name)
    this.version(1).stores({
      people: 'id, kind, display_name',
      members: 'person_id, role',
      guests: 'person_id, host_member_id',
      presence_events: 'id, person_id, at, [person_id+at]',
      visits: 'id, guest_id, arrived_at',
      chore_definitions: 'id, active',
      chore_instances: 'id, definition_id, due_day, assignee_id, status, [assignee_id+due_day]',
      chore_swaps: 'id, instance_id, status',
      proposal_types: 'key',
      proposals: 'id, status, created_at, auto_approve_at',
      proposal_votes: '[proposal_id+voter_id], proposal_id',
      resources: 'id, kind, bookable',
      bookings: 'id, resource_id, starts_at, status, [resource_id+starts_at]',
      occupancy: 'id, resource_id, ended_at',
      queue_entries: 'id, resource_id, person_id, status, joined_at',
      matches: 'id, resource_id, played_at',
      ratings: '[person_id+resource_id], resource_id',
      bills: 'id, active',
      bill_instances: 'id, bill_id, due_day',
      bill_payments: 'id, bill_instance_id',
      expenses: 'id, spent_at, paid_by_member_id',
      settlements: 'id, settled_at',
      food_orders: 'id, status, ordered_at',
      supply_items: 'id, state',
      supply_events: 'id, item_id, at',
      notes: 'id, created_at, pinned_to_room_id',
      tasks: 'id, status, assigned_to, due_day',
      wiki_pages: 'id, slug',
      checklists: 'id, kind',
      checklist_runs: 'id, day',
      space_mode: 'id',
      ramadan_periods: 'year',
      settings: 'id',
      floor_plans: 'id, updated_at',
      room_labels: 'room_id, plan_id',
      face_templates: 'id, person_id',

      outbox: 'id, queuedAt',
      rejections: 'id, at',
      meta: 'key',
    })
  }
}

/** Primary key for each mirrored table — several are not plain `id`. */
export const PRIMARY_KEY: Record<string, string | string[]> = {
  members: 'person_id',
  guests: 'person_id',
  proposal_types: 'key',
  proposal_votes: ['proposal_id', 'voter_id'],
  ratings: ['person_id', 'resource_id'],
  ramadan_periods: 'year',
  room_labels: 'room_id',
}

export async function getMeta<T>(db: OfficeDb, key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row ? (row.value as T) : fallback
}

export async function setMeta(db: OfficeDb, key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

export const CURSORS_KEY = 'sync.cursors'
export const CLOCK_KEY = 'clock.skew'
export const DEVICE_KEY = 'device.id'
